import 'react-big-calendar/lib/css/react-big-calendar.css'

import { ExternalStyles } from 'bold-ui'
import { addMinutes, getDay, isBefore, max, min, startOfDay, subMinutes } from 'date-fns'
import { subSeconds } from 'date-fns/esm'
import React, { useCallback, useMemo } from 'react'
import { Components, View } from 'react-big-calendar'

import { TimeInterval } from '.'
import { AgendaDayPeriod } from './AgendaDayPeriod'
import { AgendaEvent } from './AgendaEvent'
import { AgendaEventProps, AgendaEventWrapper } from './AgendaEventWrapper'
import { defaultMessages } from './agendaLocalizer'
import { AgendaTimeSlotProps, AgendaTimeSlotWrapper } from './AgendaTimeSlotWrapper'
import { AgendaToolbar } from './AgendaToolbar'
import { AgendaEventModel, AgendaMessages, DateInterval, SlotStatus, WeekTimeIntervals } from './model'
import { computeAvailableSlots, computeOccupiedSlots, computeTimeUntilNextSlot, timeIntervalInDay } from './utils'

interface TimeSlotProps {
  children: React.ReactNode
  resource: null
  value: number
}

export interface AgendaComponents<TEvent extends AgendaEventModel> {
  Event?: React.ComponentType<AgendaEventProps<TEvent>>
  TimeSlot?: React.ComponentType<AgendaTimeSlotProps>
  AvailableSlotHover?: React.ComponentType<AgendaTimeSlotProps>
}

export interface AgendaProps<TEvent extends AgendaEventModel> {
  date?: Date
  getNow?(): Date
  events?: TEvent[]
  view?: View
  availableTimes?: WeekTimeIntervals
  stepInMinutes?: number
  disablePastDates?: boolean
  disablePartialSlots?: boolean
  showUnavailableLimits?: boolean
  showToolbar?: boolean

  style?: ExternalStyles

  messages?: AgendaMessages
  components?: AgendaComponents<TEvent>

  onSelectSlot?(start: Date, end: Date): void

  onNavigate?(newStart: Date, newEnd: Date): void
}

export function Agenda<TEvent extends AgendaEventModel = AgendaEventModel>(props: AgendaProps<TEvent>) {
  const {
    date = new Date(),
    getNow = () => new Date(),
    events = [],
    stepInMinutes = 30,
    disablePastDates = true,
    disablePartialSlots = true,
    showUnavailableLimits = false,
    components: { AvailableSlotHover, TimeSlot, Event = AgendaEvent },
    messages = defaultMessages,
    availableTimes = {},
    showToolbar = false,
    view,
    style: externalStyles,
    onSelectSlot,
    onNavigate,
  } = props

  const { isAvailable, minTime, maxTime } = useMemo(() => computeAvailableSlots(availableTimes), [availableTimes])

  const [eventsMinStart, eventsMaxEnd] = useMemo(
    () => (events.length ? [min(events.map((e) => e.start)), max(events.map((e) => e.end))] : [undefined, undefined]),
    [events]
  )

  const minStart = eventsMinStart ? min([eventsMinStart, minTime(date)]) : minTime(date)
  const maxTimeOfDay = eventsMaxEnd ? max([eventsMaxEnd, maxTime(date)]) : maxTime(date)
  const maxEnd = computeTimeUntilNextSlot(maxTimeOfDay, stepInMinutes)
  const slotsStart = max([showUnavailableLimits ? subMinutes(minStart, stepInMinutes) : minStart, startOfDay(date)])
  const { isOccupied } = useMemo(() => computeOccupiedSlots(slotsStart, stepInMinutes, events), [
    events,
    slotsStart,
    stepInMinutes,
  ])
  const getSlotStatus = useCallback(
    (slot) => (isAvailable(slot) && (isOccupied(slot) || SlotStatus.AVAILABLE)) || SlotStatus.UNAVAILABLE,
    [isAvailable, isOccupied]
  )

  const calendarComponents = useMemo<Components<TEvent>>(
    () => ({
      toolbar: AgendaToolbar,
      eventWrapper: (props) => <AgendaEventWrapper {...props} children={Event} />,
      timeSlotWrapper: ({ value, ...rest }: TimeSlotProps) => {
        const end = addMinutes(value, stepInMinutes)
        const slot = {
          start: value,
          end: end,
        }
        const currStatus = getSlotStatus(slot)
        const prevStatus =
          +value !== +slotsStart &&
          getSlotStatus({
            start: addMinutes(value, -stepInMinutes),
            end: value,
          })
        return (
          <AgendaTimeSlotWrapper
            {...rest}
            start={value}
            end={end.getTime()}
            firstOfStatus={
              currStatus !== prevStatus &&
              !(currStatus === SlotStatus.PARTIALLY_OCCUPIED && prevStatus === SlotStatus.OCCUPIED)
            }
            disablePastDates={disablePastDates}
            disablePartialSlots={disablePartialSlots}
            messages={messages}
            status={currStatus}
            components={{ AvailableSlotHover, TimeSlot }}
          />
        )
      },
    }),
    [
      AvailableSlotHover,
      Event,
      TimeSlot,
      disablePartialSlots,
      disablePastDates,
      getSlotStatus,
      messages,
      slotsStart,
      stepInMinutes,
    ]
  )

  const handleSelectSlot =
    onSelectSlot &&
    ((slot: DateInterval) =>
      (disablePastDates && isBefore(slot.start, Date.now())) || !isAvailable(slot) || isOccupied(slot)
        ? false
        : onSelectSlot(slot.start, slot.end))

  const handleNavigate =
    onNavigate &&
    ((newDate: Date) => {
      const maxTimeOfNewDay = computeTimeUntilNextSlot(maxTime(newDate), stepInMinutes)
      return onNavigate(minTime(newDate), maxTimeOfNewDay)
    })

  const periods: TimeInterval[] = availableTimes[getDay(date)]

  return (
    <>
      {(periods?.length ? periods : [undefined]).map((interval, idx, arr) => {
        const timeInterval = interval ? timeIntervalInDay(date, interval) : undefined
        return (
          <AgendaDayPeriod<TEvent>
            key={`${timeInterval?.start}|${timeInterval?.end}`}
            start={timeInterval?.start}
            end={subSeconds(timeIntervalInDay(date, arr[idx + 1])?.start, 1)}
            slotsStart={slotsStart}
            slotsEnd={maxEnd}
            isFirst={idx === 0}
            isLast={idx === arr.length - 1}
            onSelectSlot={handleSelectSlot}
            onNavigate={handleNavigate}
            components={calendarComponents}
            date={date}
            events={events}
            getNow={getNow}
            showToolbar={showToolbar}
            showUnavailableLimits={showUnavailableLimits}
            stepInMinutes={stepInMinutes}
            style={externalStyles}
            view={view}
          />
        )
      })}
    </>
  )
}
